import React, { Component } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import FootballPredictorCard from '../../../components/FootballPredictorCard';
import FixedHeader from '../../../components/FixedHeader';
import SocialMedia from '../../../components/SocialMedia';
import GlassCard from '../../../components/GlassCard';
import './styles.css';

export default class FootballPredictorSmallSize extends Component {
  constructor(props) {
    super(props);
    this.state = {
      predictions: [],
      selectedLeague: null,
      homeFavoritesOnly: false
    };
    this.cardRefs = new Map();
  }

  componentDidMount() {
    this.loadPredictions();
  }

  loadPredictions = async () => {
    try {
      const response = await fetch('/football-predictor.json');
      const data = await response.json();
      this.setState({ predictions: this.addUniqueIds(data) });
    } catch (error) {
      console.error('Error loading predictions:', error);
    }
  };

  addUniqueIds = (predictions) => {
    const counts = new Map();
    return predictions.map((prediction) => {
      const date = prediction.date || 'no-date';
      const baseId = `${prediction.home_team_id}-${prediction.away_team_id}-${date}`;
      const nextCount = (counts.get(baseId) || 0) + 1;
      counts.set(baseId, nextCount);
      return {
        ...prediction,
        __uid: `${baseId}-${nextCount}`
      };
    });
  };

  getLeagueLabel = (league) => {
    if (!league) {
      return '';
    }
    return league
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  getPredictionId = (prediction) => {
    if (prediction.__uid) {
      return prediction.__uid;
    }
    const date = prediction.date || 'no-date';
    return `${prediction.home_team_id}-${prediction.away_team_id}-${date}`;
  };

  setCardRef = (id) => (node) => {
    if (node) {
      this.cardRefs.set(id, node);
    } else {
      this.cardRefs.delete(id);
    }
  };

  getVisiblePredictions = (state = this.state) => {
    const { predictions, selectedLeague, homeFavoritesOnly } = state;
    return predictions.filter((prediction) => {
      if (selectedLeague && prediction.league !== selectedLeague) {
        return false;
      }
      if (homeFavoritesOnly && !(prediction.prob_home_win > prediction.prob_away_win)) {
        return false;
      }
      return true;
    });
  };

  capturePositions = (ids) => {
    const positions = new Map();
    ids.forEach((id) => {
      const node = this.cardRefs.get(id);
      if (node) {
        positions.set(id, node.getBoundingClientRect());
      }
    });
    return positions;
  };

  playFlipAnimation = (previousPositions, nextIds) => {
    requestAnimationFrame(() => {
      nextIds.forEach((id) => {
        const node = this.cardRefs.get(id);
        const first = previousPositions.get(id);
        if (!node || !first) {
          return;
        }
        const last = node.getBoundingClientRect();
        const dx = first.left - last.left;
        const dy = first.top - last.top;
        if (dx !== 0 || dy !== 0) {
          node.animate(
            [
              { transform: `translate(${dx}px, ${dy}px)` },
              { transform: 'translate(0, 0)' }
            ],
            { duration: 300, easing: 'ease-out' }
          );
        }
      });
    });
  };

  toggleLeagueFilter = (league) => {
    const prevVisible = this.getVisiblePredictions();
    const prevIds = prevVisible.map(this.getPredictionId);
    const prevPositions = this.capturePositions(prevIds);
    this.setState(
      (prevState) => ({
        selectedLeague: prevState.selectedLeague === league ? null : league
      }),
      () => {
        const nextIds = this.getVisiblePredictions().map(this.getPredictionId);
        this.playFlipAnimation(prevPositions, nextIds);
      }
    );
  };

  toggleHomeFavorites = () => {
    const prevVisible = this.getVisiblePredictions();
    const prevIds = prevVisible.map(this.getPredictionId);
    const prevPositions = this.capturePositions(prevIds);
    this.setState(
      (prevState) => ({
        homeFavoritesOnly: !prevState.homeFavoritesOnly
      }),
      () => {
        const nextIds = this.getVisiblePredictions().map(this.getPredictionId);
        this.playFlipAnimation(prevPositions, nextIds);
      }
    );
  };

  render() {
    const { predictions, selectedLeague, homeFavoritesOnly } = this.state;
    const availableLeagues = Array.from(
      new Set(predictions.map((prediction) => prediction.league).filter(Boolean))
    ).sort();
    const visiblePredictions = this.getVisiblePredictions();

    return (
      <Container fluid className="p-0" style={{ backgroundColor: 'var(--dashboard-bg)', minHeight: '100vh', color: 'var(--text)' }}>
        <FixedHeader />

        <Container className="px-4 pb-4" style={{ paddingTop: '110px' }}>
          <Row className="justify-content-center mb-4">
            <GlassCard className="disclaimer">
              <h4><b>Football Predictor</b></h4>
              <br />
              Behold our statistics-powered football prediction system!
              <br />
              <br />
              This experimental tool uses statistics to analyze upcoming matches and predict outcomes.
              <br />
              <br />
              <b>Remember:</b> This is just for fun!
              <br />
              Predictions are based on available data and should not be used for betting purposes.
            </GlassCard>
          </Row>

          <div className='small-content'>
            <Row className="justify-content-center mb-4 card-container-small">
              {availableLeagues.length > 0 && (
                <GlassCard className="filters dashboard-card mb-4 w-100">
                  <div className="filters-title">Use these to filter the predictions</div>
                  <div className="filters-buttons">
                    <button
                      type="button"
                      className={`filter-button${homeFavoritesOnly ? ' active' : ''}`}
                      onClick={this.toggleHomeFavorites}
                    >
                      Home Favorites
                    </button>
                    {availableLeagues.map((league) => (
                      <button
                        key={league}
                        type="button"
                        className={`filter-button${selectedLeague === league ? ' active' : ''}`}
                        onClick={() => this.toggleLeagueFilter(league)}
                      >
                        {this.getLeagueLabel(league)}
                      </button>
                    ))}
                  </div>
                </GlassCard>
              )}
              {predictions.length === 0 ? (
                <div className="no-records-container">
                  <GlassCard className="no-records-card">
                    <h3 className="no-records-title">
                      Oh no, there are no predictions for now!
                      Please come back later...
                    </h3>
                    <div className="no-records-image-wrapper">
                      <img
                        src="/no_records.png"
                        alt="No predictions available"
                        className="no-records-image"
                      />
                    </div>
                  </GlassCard>
                </div>
              ) : (
                <Row className="predictions-grid">
                  {visiblePredictions.map((prediction) => {
                    const predictionId = this.getPredictionId(prediction);
                    return (
                      <Col key={predictionId} className="prediction-col" xs={12}>
                        <div className="prediction-card-wrapper" ref={this.setCardRef(predictionId)}>
                          <FootballPredictorCard
                            team1={prediction.home_team}
                            team1Id={prediction.home_team_id}
                            team2={prediction.away_team}
                            team2Id={prediction.away_team_id}
                            team1WinProb={prediction.prob_home_win}
                            team2WinProb={prediction.prob_away_win}
                            avgGoals={prediction.prob_amount_goals}
                          />
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </Row>
          </div>
        </Container>

        <SocialMedia isSmall={true} />
      </Container>
    );
  }
}
